/**
 * Asset recognition service
 * Handles API calls for asset recognition via photo scanning
 */

import axios from "axios";
import { CORE_URL } from "../constants/api";
import { VITE_CORE_API_URL } from "../constants/env";
import type {
	AssetRecognitionRequest,
	AssetRecognitionResponse,
	GPSLocation,
} from "../models/assets/asset-recognition.model";
import { auth } from "./auth-service";

/**
 * Get high-accuracy GPS location
 * Rejects if accuracy is too low (> 100m) or if location services are disabled
 */
export async function getHighAccuracyLocation(): Promise<GPSLocation> {
	if (!navigator.geolocation) {
		throw new Error("Location services are not supported by your browser");
	}

	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude, accuracy } = position.coords;

				// Validate accuracy - reject if using IP location or low accuracy
				if (accuracy > 100) {
					reject(
						new Error(
							`GPS accuracy insufficient: ${accuracy.toFixed(0)}m. Please ensure location services are enabled and you have a clear view of the sky.`,
						),
					);
					return;
				}

				resolve({ latitude, longitude, accuracy });
			},
			(error) => {
				let message = "Unable to retrieve your location";

				switch (error.code) {
					case error.PERMISSION_DENIED:
						message =
							"Location access denied. Please enable location services in your browser settings.";
						break;
					case error.POSITION_UNAVAILABLE:
						message =
							"Location information unavailable. Please check your device settings.";
						break;
					case error.TIMEOUT:
						message = "Location request timed out. Please try again.";
						break;
				}

				reject(new Error(message));
			},
			{
				enableHighAccuracy: true, // Force GPS, not IP
				timeout: 30000, // 30 seconds
				maximumAge: 0, // No cached locations
			},
		);
	});
}

/**
 * Recognize an asset from a photo
 */
export async function recognizeAsset(
	request: AssetRecognitionRequest,
): Promise<AssetRecognitionResponse> {
	const token = await auth.currentUser?.getIdToken();

	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await axios.post<{
		status: string;
		data: AssetRecognitionResponse;
		message?: string;
		code?: string;
	}>(`${VITE_CORE_API_URL}${CORE_URL}/submission/asset/recognize`, request, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});

	if (response.data.status !== "success") {
		throw new Error(response.data.message || "Failed to recognize asset");
	}

	return response.data.data;
}

/**
 * Submit a correction for a recognition attempt
 */
export async function correctRecognition(
	attemptId: number,
	correctedAssetId: number,
	wasCorrect: boolean,
): Promise<void> {
	const token = await auth.currentUser?.getIdToken();

	if (!token) {
		throw new Error("Not authenticated");
	}

	await axios.post(
		`${VITE_CORE_API_URL}${CORE_URL}/submission/asset/recognize/${attemptId}/correct`,
		{
			correctedAssetId,
			wasCorrect,
		},
		{
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		},
	);
}
